import { useEffect, useRef, useState } from "react";

/**
 * Detects golf-swing-like acceleration spikes from the device's accelerometer.
 *
 * - iOS 13+ requires user-gesture permission via DeviceMotionEvent.requestPermission().
 * - Threshold: magnitude (including gravity) > ~35 m/s² peak.
 * - Cooldown: 1500 ms between detections to avoid multi-counts from follow-through.
 *
 * Returns:
 *   supported     – true if DeviceMotionEvent exists in this browser
 *   permission    – "granted" | "denied" | "prompt"  (prompt = needs user gesture on iOS)
 *   liveMagnitude – current smoothed acceleration magnitude (debug indicator)
 *   requestPermission()
 */
export function useSwingDetector(enabled, onSwing) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("granted");
  const [liveMagnitude, setLiveMagnitude] = useState(0);
  const lastFireRef = useRef(0);
  const onSwingRef = useRef(onSwing);
  onSwingRef.current = onSwing;

  useEffect(() => {
    const has = typeof window !== "undefined" && typeof window.DeviceMotionEvent !== "undefined";
    setSupported(has);
    if (has && typeof window.DeviceMotionEvent.requestPermission === "function") {
      setPermission("prompt");
    }
  }, []);

  useEffect(() => {
    if (!enabled || !supported || permission !== "granted") return;

    const COOLDOWN_MS = 1500;
    const THRESHOLD = 35; // m/s² peak, including gravity baseline ~9.8
    let smoothed = 0;

    const handler = (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      smoothed = smoothed * 0.7 + mag * 0.3;
      setLiveMagnitude(smoothed);
      if (mag > THRESHOLD && Date.now() - lastFireRef.current > COOLDOWN_MS) {
        lastFireRef.current = Date.now();
        onSwingRef.current?.(mag);
      }
    };
    window.addEventListener("devicemotion", handler);
    return () => window.removeEventListener("devicemotion", handler);
  }, [enabled, supported, permission]);

  const requestPermission = async () => {
    if (typeof window.DeviceMotionEvent?.requestPermission === "function") {
      try {
        const res = await window.DeviceMotionEvent.requestPermission();
        setPermission(res === "granted" ? "granted" : "denied");
        return res;
      } catch {
        setPermission("denied");
        return "denied";
      }
    }
    setPermission("granted");
    return "granted";
  };

  return { supported, permission, liveMagnitude, requestPermission };
}
